// The Math Proficiency tab's pure logic — the labels, the ordering, the bar's
// arithmetic and the file. Nothing here renders, so all of it is testable
// without a DOM and none of it can drift from what the panel draws.
//
// Named for what a row IS rather than for the tab, so it cannot collide with
// `MathProficiency.js` on a case-insensitive filesystem — where the two would
// be one file, and the second written would silently replace the first.

import { EM_DASH } from "./studentReportTable";

// The three FIRM levels, left to right as 1 -> 3, so the key reads in the same
// direction as the bar below it. The wording is the product's own — "Ready to
// Lead" is what the rubric modal and the Class Insights key already call
// Level 3 — so two screens cannot describe the same badge differently.
export const KEY_LEVELS = [
  { level: 1, label: "Needs\nSupport", color: "#BC165D" },
  { level: 2, label: "Needs\nPractice", color: "#9B6B03" },
  { level: 3, label: "Ready\nto Lead", color: "#006B3B" },
];

// The bar, segment by segment: the fill, and the darker chip that carries the
// count so the number stays legible on every fill (node 3813:62849).
// This order is the bar's left-to-right order; nothing in the arithmetic
// depends on it.
export const BAR_SEGMENTS = [
  { level: 1, field: "level_1", fill: "#FE1E7E", chip: "#BC165D" },
  { level: 2, field: "level_2", fill: "#EAB308", chip: "#9B6B03" },
  { level: 3, field: "level_3", fill: "#00A35A", chip: "#007A35" },
];

// A work-in-progress standard reads "WIP", the same word the Class Insights
// matrix uses; the tab's definition paragraph explains it.
export const NEED_MORE_DATA = "WIP";

// What "WIP" means, in the pill's tooltip and in its accessible description.
export const WIP_DEFINITION = "WIP means fewer than 5 questions answered";

export const PROFICIENCY_SORT_KEYS = {
  STANDARD: "standard",
  LAST_LESSON: "last_lesson",
  LEVEL: "proficiency_level",
};

// Firm levels sort before work-in-progress ones. This is the rank the Class
// Insights table already sorts by (Proficiency/proficiencyTable.js): the same
// six statuses must not order differently on two screens.
const STATUS_RANK = {
  LEVEL_1: 0,
  LEVEL_2: 1,
  LEVEL_3: 2,
  WIP_LOW: 3,
  WIP_MID: 4,
  WIP_HIGH: 5,
};

const FIRM_LABEL = {
  LEVEL_1: "Level 1",
  LEVEL_2: "Level 2",
  LEVEL_3: "Level 3",
};

export const ALL_GRADES = "All Grades";
export const ALL_DOMAINS = "All Domains";

/** A grade as the dropdown spells it: "Grade 5", but "Kindergarten" for K. */
export const gradeLabel = (grade) => {
  if (!grade) return ALL_GRADES;
  return String(grade).toUpperCase() === "K" ? "Kindergarten" : `Grade ${grade}`;
};

/** The PDF's filter line: "Grade: Grade 5 · Domain: All Domains". */
export const proficiencyFilterLine = ({ grade, domainName }) =>
  `Grade: ${gradeLabel(grade)} · Domain: ${domainName || ALL_DOMAINS}`;

/**
 * The date under Last Lesson: "06/20/26".
 *
 * Read off the date PARTS rather than through `new Date`, which parses a bare
 * "2026-06-20" as UTC midnight and renders it as the 19th anywhere west of
 * Greenwich — which is the whole of this product's user base.
 */
export const lastLessonText = (value) => {
  if (!value) return EM_DASH;
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return EM_DASH;
  return `${month}/${day}/${year.slice(2)}`;
};

/** What the Current Proficiency Level column says for one row. */
export const proficiencyText = (row) => {
  if (!row?.status) return EM_DASH;
  return FIRM_LABEL[row.status] || NEED_MORE_DATA;
};

/** The firm level a row draws a tag for, or null for every other state. */
export const firmLevel = (row) => (row?.status ? row.math_level ?? null : null);

/** A chip's label: the ordinal counts from this child's FIRST answer. */
export const questionLabel = (attempt) => `Q${attempt.ordinal}`;

/**
 * The bar's segments, sized by their share of the FIRM standards only.
 *
 * Work-in-progress and untouched standards are deliberately out of the
 * denominator: the bar answers "of what we have measured, where does this
 * child stand", and folding in the unmeasured would shrink every segment by an
 * amount nothing on screen explains. Those two counts are stated separately,
 * in words, directly beneath it.
 */
export const distributionSegments = (distribution = {}) => {
  const counts = BAR_SEGMENTS.map((segment) => ({
    ...segment,
    count: Number(distribution?.[segment.field]) || 0,
  }));
  const total = counts.reduce((sum, segment) => sum + segment.count, 0);
  if (!total) return [];
  return counts
    .filter((segment) => segment.count > 0)
    .map((segment) => ({
      ...segment,
      widthPct: (segment.count * 100) / total,
      label: `${segment.count} ${segment.count === 1 ? "Standard" : "Standards"}`,
    }));
};

/** Ascending, descending, then back to the order the server sent. */
export const nextProficiencySort = (current, column) => {
  if (!current || current.column !== column) return { column, direction: "asc" };
  if (current.direction === "asc") return { column, direction: "desc" };
  return null;
};

const sortValue = (row, column) => {
  switch (column) {
    case PROFICIENCY_SORT_KEYS.STANDARD:
      return row.standard_code || null;
    case PROFICIENCY_SORT_KEYS.LAST_LESSON:
      return row.last_lesson_date || null;
    case PROFICIENCY_SORT_KEYS.LEVEL:
      return STATUS_RANK[row.status] ?? null;
    default:
      return null;
  }
};

/**
 * Sort a COPY, never the array in place: the rows are the fetch's state, and
 * sorting them in place makes "sort" a mutation the next render inherits.
 *
 * A missing value sorts LAST IN BOTH DIRECTIONS. A standard with no evidence
 * is not the weakest standard, and letting it float to the top of an ascending
 * sort would say exactly that.
 */
export const sortProficiencyRows = (rows = [], sort = null) => {
  if (!sort?.column) return rows;
  const factor = sort.direction === "desc" ? -1 : 1;
  return [...rows].sort((left, right) => {
    const a = sortValue(left, sort.column);
    const b = sortValue(right, sort.column);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    if (a === b) return 0;
    return (a < b ? -1 : 1) * factor;
  });
};

// ---- the CSV -------------------------------------------------------------

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvLine = (cells) => cells.map(csvCell).join(",");

// An unknown is one consistent EMPTY cell, as the Detail tab's file already
// spells it: an em dash is what a reader wants and what a spreadsheet cannot
// average.
const csvValue = (text) => (text === EM_DASH ? "" : text);

// The screen's columns, the Standard split into its code and its name.
export const PROFICIENCY_CSV_HEADERS = [
  "Standard",
  "Description",
  "Most Recent",
  "Proficiency",
  "Last 10 Questions (newest first)",
];

// "Q12=correct|Q11=incorrect": one cell a spreadsheet can hold and a person can
// read left to right in the order the grid draws it. A tick glyph would be
// prettier and would not survive Excel's encoding guess.
const attemptsCell = (attempts = []) =>
  attempts
    .map((attempt) => `${questionLabel(attempt)}=${attempt.correct ? "correct" : "incorrect"}`)
    .join("|");

/**
 * The file, built from the rows already in the browser: a dead API cannot
 * corrupt it, and it cannot disagree with what the teacher is looking at.
 *
 * It follows the FILTERS, so a teacher who narrowed to one grade gets that
 * grade — including its untouched standards, whose empty cells are the reason
 * they narrowed in the first place.
 */
export const buildProficiencyCsv = (rows = []) =>
  `${[
    csvLine(PROFICIENCY_CSV_HEADERS),
    ...rows.map((row) =>
      csvLine([
        row.standard_code,
        row.standard_name,
        csvValue(lastLessonText(row.last_lesson_date)),
        csvValue(proficiencyText(row)),
        attemptsCell(row.attempts),
      ])
    ),
  ].join("\n")}\n`;
